(function (window, document, $) {
    'use strict';

    if (!$) {
        return;
    }

    const MAX_DECK_SIZE = 6;
    const OVERSCAN_ROWS = 4;
    const MAX_RENDERED_CARDS = 78;
    const RESIZE_DEBOUNCE_MS = 150;
    const SAVE_DEBOUNCE_MS = 80;
    const CARD_ASPECT_RATIO = 848 / 601;

    function translate(key, fallback, params) {
        let value = (window.I18N && window.I18N[key]) || fallback || key;
        const replacements = params || {};

        Object.keys(replacements).forEach(function (name) {
            value = String(value).replace(
                new RegExp('\\{' + name + '\\}', 'g'),
                String(replacements[name])
            );
        });

        return value;
    }

    function normalizeText(value) {
        return String(value || '')
            .toLocaleLowerCase()
            .trim()
            .replace(/\s+/g, ' ');
    }

    function parseInteger(value, fallback) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    const SORT_VALUES = new Set([
        'discovery-date',
        'level-desc',
        'level-asc',
        'attack-desc',
        'attack-asc',
        'defense-desc',
        'defense-asc'
    ]);

    function getSortCookieName(eventType) {
        const normalizedType = String(eventType || 'default').trim().toLowerCase() || 'default';
        return 'collection_sort_by_' + normalizedType.replace(/[^a-z0-9_-]/g, '_');
    }

    function readCookie(name) {
        const prefix = encodeURIComponent(name) + '=';
        const cookie = document.cookie.split('; ').find((entry) => entry.indexOf(prefix) === 0);
        return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : '';
    }

    function getPersistedSort(eventId) {
        const value = readCookie(getSortCookieName(eventId));
        return SORT_VALUES.has(value) ? value : 'discovery-date';
    }

    function persistSort(eventId, value) {
        if (!SORT_VALUES.has(value)) {
            return;
        }

        document.cookie = encodeURIComponent(getSortCookieName(eventId))
            + '=' + encodeURIComponent(value)
            + '; max-age=31536000; path=/; SameSite=Lax';
    }

    function escapeCssUrl(url) {
        return String(url || '').replace(/["\\\n\r\f]/g, function (character) {
            return '\\' + character;
        });
    }

    function debounce(callback, delay) {
        let timer = null;

        return function () {
            const args = arguments;
            const context = this;
            window.clearTimeout(timer);
            timer = window.setTimeout(function () {
                callback.apply(context, args);
            }, delay);
        };
    }

    function readPayload() {
        const payloadElement = document.getElementById('collection-data');
        if (!payloadElement) {
            return null;
        }

        try {
            const payload = JSON.parse(payloadElement.textContent || '{}');
            return payload && typeof payload === 'object' ? payload : null;
        } catch (error) {
            console.error('[Collection] Invalid collection payload.', error);
            return null;
        }
    }

    class CollectionApp {
        constructor(payload) {
            this.payload = payload;
            this.eventType = String(
                payload.eventType !== undefined ? payload.eventType : (payload.event_type || 'default')
            );
            this.viewport = document.getElementById('collection-viewport');
            this.spacer = document.getElementById('collection-spacer');
            this.visibleCards = document.getElementById('collection-visible-cards');
            this.emptyState = document.getElementById('collection-empty-state');
            this.deckContainer = document.querySelector('#deck-building .collection-cards');
            this.deckSelect = document.getElementById('deck-list');
            this.deckBuilding = document.getElementById('deck-building');
            this.deckLoadingIndicator = document.getElementById('deck-loading-indicator');
            this.deckStatus = document.getElementById('deck-switch-status');

            this.bonusDefinitions = payload.bonusDefinitions || payload.bonus_definitions || {};
            this.levelLabels = payload.levelLabels || payload.level_labels || {};
            this.raceLabels = payload.raceLabels || payload.race_labels || {};
            this.labels = Object.assign({
                add: translate('js.deck.add', 'Add to Deck'),
                remove: translate('js.deck.remove', 'Remove'),
                full: translate('js.deck.full', 'Deck full'),
                loading: translate('collection.deck_loading', 'Loading deck…'),
                loadError: translate('collection.deck_load_error', 'Unable to load this deck.'),
                saveError: translate('collection.deck_save_error', 'Unable to save this deck.'),
                empty: translate('collection.no_results', 'No cards match these filters.')
            }, payload.labels || {});
            this.endpoints = Object.assign({
                updateDeck: '/ajax/update-deck',
                activateDeck: '/ajax/activate-deck'
            }, payload.endpoints || {});

            const cards = Array.isArray(payload.cards) ? payload.cards : [];
            const normalizedCards = cards
                .map((card, index) => this.normalizeCard(card, index))
                .filter((card) => card.id > 0);
            const cardsById = new Map(normalizedCards.map((card) => [card.id, card]));
            const decks = this.normalizeDecks(payload.decks, cardsById);
            const decksById = new Map(decks.map((deck) => [deck.id, deck]));
            let currentDeckId = parseInteger(
                payload.activeDeckId !== undefined ? payload.activeDeckId : payload.active_deck_id,
                0
            );

            if (!decksById.has(currentDeckId)) {
                const activeDeck = decks.find((deck) => deck.active);
                currentDeckId = activeDeck ? activeDeck.id : (decks[0] ? decks[0].id : 0);
            }

            const currentDeck = decksById.get(currentDeckId);
            const deckCardIds = new Set(currentDeck ? currentDeck.cards.map((entry) => entry.id) : []);

            this.state = {
                allCards: normalizedCards,
                filteredCards: normalizedCards.slice(),
                cardsById: cardsById,
                decks: decks,
                decksById: decksById,
                deckCardIds: deckCardIds,
                currentDeckId: currentDeckId,
                filters: {
                    name: '',
                    attackMin: 0,
                    defenseMin: 0,
                    rarities: [],
                    races: [],
                    bonuses: []
                },
                sortBy: getPersistedSort(this.eventType)
            };

            this.columns = 0;
            this.gap = 0;
            this.cardWidth = 0;
            this.rowHeight = 0;
            this.layoutVersion = 0;
            this.dataVersion = 0;
            this.lastRenderKey = '';
            this.renderFrame = null;
            this.circleFrame = null;
            this.enhancementScopes = new Set();
            this.resizeObserver = null;
            this.destroyed = false;
            this.operationChain = Promise.resolve();
            this.persistedDeckSignatures = new Map();
            this.pendingSave = null;
            this.pendingSavePromise = null;
            this.pendingSaveResolve = null;
            this.pendingSaveReject = null;
            this.saveTimer = null;
            this.deckSwitchSequence = 0;
            this.deckSwitchInProgress = false;
            this.suppressDeckClickUntil = 0;

            decks.forEach((deck) => {
                this.persistedDeckSignatures.set(deck.id, this.deckSignature(deck.cards));
            });

            this.onScroll = this.scheduleRender.bind(this);
            this.onResize = debounce(() => {
                // ResizeObserver also fires for height-only changes caused by
                // CircleType. Forcing a render here would replace every card,
                // reapply CircleType, and start a render loop (notably in
                // Firefox), which can remove action buttons mid-click.
                this.recalculateLayout(false);
            }, RESIZE_DEBOUNCE_MS);
        }

        normalizeCard(rawCard, sourceIndex) {
            const raw = rawCard && typeof rawCard === 'object' ? rawCard : {};
            const id = parseInteger(raw.id, 0);
            const race = String(raw.race || '').trim().toLocaleLowerCase();
            const level = Math.max(0, parseInteger(raw.level, 0));
            const bonuses = Array.isArray(raw.bonuses)
                ? raw.bonuses.map((bonus) => this.normalizeBonus(bonus)).filter(Boolean)
                : [];
            const discoveryDate = String(
                raw.discoveryDate !== undefined ? raw.discoveryDate : (raw.discovery_date || '')
            );
            const imageUrl = id > 0 ? '/cards/' + id + '.webp' : '';
            const name = String(raw.name || '');

            return {
                id: id,
                name: name,
                normalizedName: normalizeText(name),
                level: level,
                levelLabel: String(
                    raw.levelLabel
                    || raw.level_label
                    || this.levelLabels[level]
                    || ''
                ),
                race: race,
                raceLabel: String(
                    raw.raceLabel
                    || raw.race_label
                    || this.raceLabels[race]
                    || race
                ),
                attack: parseInteger(raw.attack, 0),
                defense: parseInteger(raw.defense, 0),
                bonuses: bonuses,
                bonusNames: new Set(bonuses.map((bonus) => bonus.name)),
                description: String(raw.description || ''),
                discoveryDate: discoveryDate,
                discoveryTimestamp: this.parseDiscoveryTimestamp(discoveryDate),
                catalystLocked: Boolean(raw.catalystLocked || raw.catalyst_locked),
                imageUrl: imageUrl,
                sourceIndex: sourceIndex
            };
        }

        normalizeBonus(rawBonus) {
            const raw = rawBonus && typeof rawBonus === 'object' ? rawBonus : {};
            const name = String(raw.name || '');
            if (!name) {
                return null;
            }

            const definition = this.bonusDefinitions[name] || {};
            const value = raw.value === undefined || raw.value === null ? '' : String(raw.value);
            const definitionText = String(
                raw.definition !== undefined ? raw.definition : (definition.definition || '')
            );
            const halfValue = Math.max(1, Math.round((Number.parseFloat(value) || 0) / 2));
            const renderedDefinition = definitionText
                .replace(/#VALUE#/g, value)
                .replace(/#HALF_VALUE#/g, String(halfValue));
            const displayName = String(raw.displayName || raw.display_name || definition.name || name);

            return {
                name: name,
                value: value,
                emoji: String(raw.emoji || definition.emoji || ''),
                title: String(raw.title || (displayName + ' : ' + renderedDefinition))
            };
        }

        normalizeDecks(rawDecks, cardsById) {
            const decks = Array.isArray(rawDecks) ? rawDecks : [];

            return decks.map(function (rawDeck) {
                const raw = rawDeck && typeof rawDeck === 'object' ? rawDeck : {};
                const entries = Array.isArray(raw.cards) ? raw.cards : [];
                const seenIds = new Set();
                const cards = entries
                    .map(function (entry, index) {
                        if (entry && typeof entry === 'object') {
                            return {
                                id: parseInteger(entry.id, 0),
                                position: parseInteger(entry.position, index)
                            };
                        }
                        return {
                            id: parseInteger(entry, 0),
                            position: index
                        };
                    })
                    .filter(function (entry) {
                        if (entry.id <= 0 || !cardsById.has(entry.id) || seenIds.has(entry.id)) {
                            return false;
                        }
                        seenIds.add(entry.id);
                        return true;
                    })
                    .sort(function (left, right) {
                        return left.position - right.position;
                    })
                    .slice(0, MAX_DECK_SIZE)
                    .map(function (entry, index) {
                        return { id: entry.id, position: index };
                    });

                return {
                    id: parseInteger(raw.id, 0),
                    name: String(raw.name || ''),
                    eloRating: parseInteger(
                        raw.eloRating !== undefined ? raw.eloRating : raw.elo_rating,
                        0
                    ),
                    active: Boolean(raw.active),
                    cards: cards
                };
            }).filter(function (deck) {
                return deck.id > 0;
            });
        }

        parseDiscoveryTimestamp(value) {
            const timestamp = Date.parse(value);
            return Number.isFinite(timestamp) ? timestamp : 0;
        }

        init() {
            if (!this.viewport || !this.spacer || !this.visibleCards || !this.deckContainer) {
                return false;
            }

            window.collectionState = this.state;
            window.collectionApp = this;

            const sortSelect = document.getElementById('sort-by-select');
            if (sortSelect && SORT_VALUES.has(this.state.sortBy)) {
                sortSelect.value = this.state.sortBy;
            }

            this.bindEvents();
            this.applyFilters(this.state.filters, this.state.sortBy, {
                preserveScroll: true,
                force: true
            });
            this.renderDeck();
            this.updateDeckUi();
            this.updateDeckQuickFilters();
            this.recalculateLayout(true);

            if (this.deckSelect && this.state.currentDeckId > 0) {
                this.deckSelect.value = String(this.state.currentDeckId);
            }

            $(document).trigger('collection:ready', [this]);
            return true;
        }

        bindEvents() {
            $(this.visibleCards)
                .on('click.virtualCollection', '.card-actions .deck', (event) => {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    const cardId = parseInteger($(event.currentTarget).closest('.card').data('id'), 0);
                    this.toggleDeckCard(cardId);
                })
                .on('click.virtualCollection', '.card-actions .zoom', (event) => {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    const cardId = parseInteger($(event.currentTarget).closest('.card').data('id'), 0);
                    this.openCardZoom(cardId);
                })
                .on('click.virtualCollection', '.card', (event) => {
                    event.stopPropagation();
                    if ($(event.target).closest('.card-actions .cta').length) {
                        return;
                    }
                    this.toggleActiveCard(event.currentTarget);
                })
                .on('keydown.virtualCollection', '.card', (event) => {
                    if ($(event.target).is('button, a, input, select, textarea')) {
                        return;
                    }
                    this.handleCollectionCardKeydown(event);
                });

            $(this.deckContainer)
                .on('click.virtualCollection', '.card-actions .deck', (event) => {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    const cardId = parseInteger($(event.currentTarget).closest('.card').data('id'), 0);
                    this.removeCardFromDeck(cardId);
                })
                .on('click.virtualCollection', '.card-actions .zoom', (event) => {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    const cardId = parseInteger($(event.currentTarget).closest('.card').data('id'), 0);
                    this.openCardZoom(cardId);
                })
                .on('click.virtualCollection', '.card', (event) => {
                    event.stopPropagation();
                    if (Date.now() < this.suppressDeckClickUntil) {
                        return;
                    }
                    if ($(event.target).closest('.card-actions .cta').length) {
                        return;
                    }
                    this.toggleActiveCard(event.currentTarget);
                });

            if (this.deckSelect) {
                $(this.deckSelect).on('change.virtualCollection', (event) => {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    this.switchDeck(parseInteger(event.currentTarget.value, 0)).catch(() => {
                        // switchDeck restores the previous selector value and
                        // reports the localized error inside the deck area.
                    });
                });
            }

            const sortSelect = document.getElementById('sort-by-select');
            if (sortSelect) {
                $(sortSelect).on('change.virtualCollection', (event) => {
                    persistSort(this.eventType, String(event.currentTarget.value || ''));
                });
            }

            $(document).on('collection:sort-end.virtualCollection', () => {
                this.suppressDeckClickUntil = Date.now() + 300;
            });

            window.addEventListener('scroll', this.onScroll, { passive: true });
            window.addEventListener('resize', this.onResize, { passive: true });
            window.addEventListener('orientationchange', this.onResize, { passive: true });
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', this.onResize, { passive: true });
            }

            if ('ResizeObserver' in window) {
                this.resizeObserver = new ResizeObserver(this.onResize);
                this.resizeObserver.observe(this.viewport);
            }
        }

        applyFilters(filters, sortBy, options) {
            const nextFilters = filters && typeof filters === 'object' ? filters : {};
            const nextSortBy = String(sortBy || this.state.sortBy || 'discovery-date');
            const normalizedFilters = {
                name: normalizeText(nextFilters.name),
                attackMin: Math.max(0, parseInteger(nextFilters.attackMin, 0)),
                defenseMin: Math.max(0, parseInteger(nextFilters.defenseMin, 0)),
                rarities: (Array.isArray(nextFilters.rarities) ? nextFilters.rarities : [])
                    .map((value) => String(value)),
                races: (Array.isArray(nextFilters.races) ? nextFilters.races : [])
                    .map((value) => String(value).toLocaleLowerCase()),
                bonuses: (Array.isArray(nextFilters.bonuses) ? nextFilters.bonuses : [])
                    .map((value) => String(value))
            };
            const raritySet = new Set(normalizedFilters.rarities);
            const raceSet = new Set(normalizedFilters.races);
            const bonusSet = new Set(normalizedFilters.bonuses);

            const filteredCards = this.state.allCards.filter(function (card) {
                if (normalizedFilters.name && !card.normalizedName.includes(normalizedFilters.name)) {
                    return false;
                }
                if (card.attack < normalizedFilters.attackMin) {
                    return false;
                }
                if (card.defense < normalizedFilters.defenseMin) {
                    return false;
                }
                if (raritySet.size > 0 && !raritySet.has(String(card.level))) {
                    return false;
                }
                if (raceSet.size > 0 && !raceSet.has(card.race)) {
                    return false;
                }
                if (bonusSet.size > 0) {
                    let hasSelectedBonus = false;
                    bonusSet.forEach(function (bonusName) {
                        if (card.bonusNames.has(bonusName)) {
                            hasSelectedBonus = true;
                        }
                    });
                    if (!hasSelectedBonus) {
                        return false;
                    }
                }
                return true;
            });

            filteredCards.sort(this.getCardComparator(nextSortBy));
            this.state.filters = normalizedFilters;
            this.state.sortBy = nextSortBy;
            this.state.filteredCards = filteredCards;
            this.dataVersion += 1;
            this.lastRenderKey = '';

            const settings = options || {};
            if (!settings.preserveScroll) {
                this.scrollCollectionToTop();
            }

            this.updateSpacerHeight();
            this.renderVisibleCards(true);
            $(document).trigger('collection:filtered', [filteredCards.length, this.state.allCards.length]);
        }

        getCardComparator(sortBy) {
            const compareSourceOrder = function (left, right) {
                return left.sourceIndex - right.sourceIndex;
            };
            const compareNumber = function (selector, direction) {
                return function (left, right) {
                    const difference = selector(left) - selector(right);
                    return difference === 0 ? compareSourceOrder(left, right) : difference * direction;
                };
            };

            switch (sortBy) {
                case 'level-asc':
                    return compareNumber((card) => card.level, 1);
                case 'level-desc':
                    return compareNumber((card) => card.level, -1);
                case 'attack-asc':
                    return compareNumber((card) => card.attack, 1);
                case 'attack-desc':
                    return compareNumber((card) => card.attack, -1);
                case 'defense-asc':
                    return compareNumber((card) => card.defense, 1);
                case 'defense-desc':
                    return compareNumber((card) => card.defense, -1);
                case 'discovery-date':
                default:
                    return compareNumber((card) => card.discoveryTimestamp, -1);
            }
        }

        scrollCollectionToTop() {
            if (!this.viewport) {
                return;
            }

            const viewportTop = this.viewport.getBoundingClientRect().top + window.scrollY;
            const searchContainer = document.getElementById('search-container');
            const searchHeight = searchContainer ? searchContainer.getBoundingClientRect().height : 0;
            const targetTop = Math.max(0, viewportTop - searchHeight);

            if (window.scrollY > targetTop + 1) {
                window.scrollTo(0, targetTop);
            }
        }

        scrollToCard(cardId) {
            const targetId = parseInteger(cardId, 0);
            const cardIndex = this.state.filteredCards.findIndex(function (card) {
                return card.id === targetId;
            });
            if (cardIndex < 0) {
                return false;
            }

            if (this.columns <= 0 || this.rowHeight <= 0) {
                this.recalculateLayout(true);
            }
            if (this.columns <= 0 || this.rowHeight <= 0) {
                return false;
            }

            const row = Math.floor(cardIndex / this.columns);
            const viewportTop = this.viewport.getBoundingClientRect().top + window.scrollY;
            const searchContainer = document.getElementById('search-container');
            const searchHeight = searchContainer && searchContainer.offsetParent !== null
                ? searchContainer.getBoundingClientRect().height
                : 0;
            window.scrollTo(0, Math.max(0, viewportTop + (row * this.rowHeight) - searchHeight));
            this.lastRenderKey = '';
            this.renderVisibleCards(true);
            return true;
        }

        recalculateLayout(forceRender) {
            if (this.destroyed || !this.viewport) {
                return;
            }

            const width = this.viewport.clientWidth;
            if (width <= 0) {
                return;
            }

            const oldColumns = this.columns;
            const oldRowHeight = this.rowHeight;
            const viewportTop = this.viewport.getBoundingClientRect().top + window.scrollY;
            const relativeScrollTop = Math.max(0, window.scrollY - viewportTop);
            const wasInsideCollection = oldColumns > 0
                && oldRowHeight > 0
                && window.scrollY >= viewportTop
                && relativeScrollTop <= this.spacer.getBoundingClientRect().height;
            const anchorIndex = wasInsideCollection
                ? Math.floor(relativeScrollTop / oldRowHeight) * oldColumns
                : null;
            const anchorOffset = wasInsideCollection
                ? relativeScrollTop % oldRowHeight
                : 0;

            // Keep the compact three-column layout on phones, use four cards
            // on portrait tablets, and reserve six columns for desktop.
            const columns = window.matchMedia('(max-width: 560px)').matches
                ? 3
                : (window.matchMedia('(max-width: 991px)').matches ? 4 : 6);
            const gap = window.matchMedia('(max-width: 1200px)').matches ? 7 : 25;
            const availableWidth = Math.max(1, width - (gap * (columns - 1)));
            const cardWidth = Math.min(240, availableWidth / columns);
            const rowHeight = (cardWidth * CARD_ASPECT_RATIO) + gap;
            const changed = columns !== this.columns
                || Math.abs(rowHeight - this.rowHeight) > 0.5
                || Math.abs(cardWidth - this.cardWidth) > 0.5;

            this.columns = columns;
            this.gap = gap;
            this.cardWidth = cardWidth;
            this.rowHeight = rowHeight;
            this.visibleCards.style.setProperty('--collection-columns', String(columns));
            this.visibleCards.style.setProperty('--collection-gap', gap + 'px');
            this.visibleCards.style.setProperty('--collection-card-width', cardWidth + 'px');

            if (changed) {
                this.layoutVersion += 1;
                this.lastRenderKey = '';
            }

            this.updateSpacerHeight();
            if (changed && anchorIndex !== null) {
                const anchorRow = Math.floor(anchorIndex / columns);
                const nextScrollTop = viewportTop
                    + (anchorRow * rowHeight)
                    + Math.min(anchorOffset, Math.max(0, rowHeight - 1));
                window.scrollTo(0, nextScrollTop);
            }
            this.renderVisibleCards(Boolean(forceRender || changed));
        }

        updateSpacerHeight() {
            if (!this.spacer || this.columns <= 0 || this.rowHeight <= 0) {
                return;
            }

            const totalRows = Math.ceil(this.state.filteredCards.length / this.columns);
            const totalHeight = totalRows > 0
                ? Math.max(0, (totalRows * this.rowHeight) - this.gap)
                : 0;
            this.spacer.style.height = totalHeight + 'px';

            if (this.emptyState) {
                this.emptyState.hidden = this.state.filteredCards.length !== 0;
                this.emptyState.textContent = this.labels.empty;
            }
        }

        scheduleRender() {
            if (this.renderFrame !== null || this.destroyed) {
                return;
            }

            this.renderFrame = window.requestAnimationFrame(() => {
                this.renderFrame = null;
                this.renderVisibleCards(false);
            });
        }

        getVisibleRange() {
            const cardCount = this.state.filteredCards.length;
            if (cardCount === 0 || this.columns <= 0 || this.rowHeight <= 0) {
                return {
                    startRow: 0,
                    startIndex: 0,
                    endIndex: 0
                };
            }

            const viewportTop = this.viewport.getBoundingClientRect().top + window.scrollY;
            const scrollTop = Math.max(0, window.scrollY - viewportTop);
            const totalRows = Math.ceil(cardCount / this.columns);
            const firstVisibleRow = Math.floor(scrollTop / this.rowHeight);
            const visibleRowCount = Math.ceil(window.innerHeight / this.rowHeight) + 1;
            const maxRenderedRows = Math.max(1, Math.floor(MAX_RENDERED_CARDS / this.columns));
            const rowsAvailableForLeadingOverscan = Math.max(
                0,
                maxRenderedRows - Math.min(visibleRowCount, maxRenderedRows)
            );
            const leadingOverscan = Math.min(OVERSCAN_ROWS, rowsAvailableForLeadingOverscan);
            const trailingOverscan = Math.min(
                OVERSCAN_ROWS,
                Math.max(0, maxRenderedRows - leadingOverscan - visibleRowCount)
            );
            const startRow = Math.max(0, firstVisibleRow - leadingOverscan);
            const endRow = Math.min(
                totalRows,
                startRow + maxRenderedRows,
                firstVisibleRow + visibleRowCount + trailingOverscan
            );

            return {
                startRow: startRow,
                startIndex: startRow * this.columns,
                endIndex: Math.min(cardCount, endRow * this.columns)
            };
        }

        renderVisibleCards(force) {
            if (this.destroyed || !this.visibleCards) {
                return;
            }

            const range = this.getVisibleRange();
            const renderKey = [
                range.startIndex,
                range.endIndex,
                this.layoutVersion,
                this.dataVersion
            ].join(':');

            if (!force && renderKey === this.lastRenderKey) {
                return;
            }
            this.lastRenderKey = renderKey;

            this.destroyCirclesIn(this.visibleCards);
            const focusedCard = document.activeElement
                ? document.activeElement.closest('#collection-visible-cards .card')
                : null;
            const focusedCardId = focusedCard ? parseInteger(focusedCard.dataset.id, 0) : 0;
            const fragment = document.createDocumentFragment();

            for (let index = range.startIndex; index < range.endIndex; index += 1) {
                const card = this.state.filteredCards[index];
                fragment.appendChild(this.createCardElement(card, false, index));
            }

            this.visibleCards.replaceChildren(fragment);
            this.visibleCards.style.transform = 'translateY('
                + (range.startRow * this.rowHeight)
                + 'px)';
            if (focusedCardId > 0) {
                const nextFocusedCard = this.visibleCards.querySelector(
                    '.card[data-id="' + focusedCardId + '"]'
                );
                if (nextFocusedCard) {
                    nextFocusedCard.focus({ preventScroll: true });
                }
            }
            this.applyEnhancementsAfterFrame(this.visibleCards);
        }

        createCardElement(card, isDeckCard, collectionIndex) {
            const cardElement = document.createElement('div');
            cardElement.tabIndex = 0;
            cardElement.setAttribute('role', 'listitem');
            cardElement.dataset.id = String(card.id);
            cardElement.dataset.discovery = card.discoveryDate;
            cardElement.dataset.name = card.name;
            cardElement.dataset.level = String(card.level);
            cardElement.dataset.catalystLocked = card.catalystLocked ? '1' : '0';
            if (!isDeckCard && Number.isFinite(collectionIndex)) {
                cardElement.dataset.collectionIndex = String(collectionIndex);
                cardElement.setAttribute('aria-posinset', String(collectionIndex + 1));
                cardElement.setAttribute('aria-setsize', String(this.state.filteredCards.length));
            }
            cardElement.className = [
                'card',
                'virtual-card',
                'has-card-image',
                'level-' + card.level,
                'race-' + card.race,
                (isDeckCard || this.state.deckCardIds.has(card.id)) ? 'in-deck' : ''
            ].filter(Boolean).join(' ');

            const image = document.createElement('img');
            image.className = 'card-image';
            image.src = card.imageUrl;
            image.alt = '';
            image.loading = 'lazy';
            image.decoding = 'async';
            image.width = 800;
            image.height = 600;
            image.draggable = false;
            cardElement.appendChild(image);

            const level = document.createElement('div');
            level.className = 'card-level tooltip';
            level.dataset.title = card.levelLabel;
            level.textContent = String(card.level + 1);
            cardElement.appendChild(level);

            const illustration = document.createElement('div');
            illustration.className = 'card-illustration';
            cardElement.appendChild(illustration);

            const name = document.createElement('div');
            name.className = 'card-name';
            name.textContent = card.name;
            cardElement.appendChild(name);

            const content = document.createElement('div');
            content.className = 'card-content';

            if (card.bonuses.length > 0) {
                const bonuses = document.createElement('ul');
                bonuses.className = 'card-bonuses bonues-' + card.bonuses.length;
                card.bonuses.forEach(function (bonus) {
                    const item = document.createElement('li');
                    item.dataset.name = bonus.name;
                    item.dataset.value = bonus.value;
                    item.className = 'tooltip';
                    item.dataset.title = bonus.title;
                    item.textContent = bonus.emoji;
                    bonuses.appendChild(item);
                });
                content.appendChild(bonuses);
            }

            if (card.description) {
                const flavorText = document.createElement('div');
                flavorText.className = 'flavor-text';
                flavorText.textContent = '«\u00a0'
                    + card.description.replace(/[.!]/g, '')
                    + '\u00a0»';
                content.appendChild(flavorText);
            }

            const stats = document.createElement('div');
            stats.className = 'card-attack-defense';

            const attack = document.createElement('span');
            attack.className = 'card-attack';
            attack.textContent = String(card.attack);
            stats.appendChild(attack);
            stats.appendChild(document.createTextNode(' / '));

            const defense = document.createElement('span');
            defense.className = 'card-defense';
            defense.textContent = String(card.defense);
            stats.appendChild(defense);

            const statBonuses = document.createElement('ul');
            statBonuses.className = 'attack-defense-bonus';
            stats.appendChild(statBonuses);
            content.appendChild(stats);
            cardElement.appendChild(content);

            const race = document.createElement('div');
            race.className = 'card-race';
            race.textContent = card.raceLabel;
            cardElement.appendChild(race);

            const actions = document.createElement('div');
            actions.className = 'card-actions';

            const zoom = document.createElement('button');
            zoom.type = 'button';
            zoom.className = 'zoom cta';
            zoom.textContent = 'Zoom';
            actions.appendChild(zoom);

            const deckAction = document.createElement('button');
            deckAction.type = 'button';
            deckAction.className = 'deck cta';
            const inDeck = isDeckCard || this.state.deckCardIds.has(card.id);
            const canAdd = this.state.deckCardIds.size < MAX_DECK_SIZE;
            deckAction.textContent = inDeck
                ? this.labels.remove
                : (canAdd ? this.labels.add : this.labels.full);
            if (!inDeck && !canAdd) {
                deckAction.classList.add('is-disabled');
                deckAction.setAttribute('aria-disabled', 'true');
            } else {
                deckAction.setAttribute('aria-disabled', 'false');
            }
            actions.appendChild(deckAction);
            cardElement.appendChild(actions);

            return cardElement;
        }

        renderDeck() {
            const deck = this.state.decksById.get(this.state.currentDeckId);
            const fragment = document.createDocumentFragment();
            this.destroyCirclesIn(this.deckContainer);

            if (deck) {
                deck.cards.forEach((entry) => {
                    const card = this.state.cardsById.get(entry.id);
                    if (card) {
                        fragment.appendChild(this.createCardElement(card, true));
                    }
                });
            }

            this.deckContainer.replaceChildren(fragment);
            this.applyEnhancementsAfterFrame(this.deckContainer);
        }

        applyEnhancementsAfterFrame(scope) {
            if (!scope) {
                return;
            }
            this.enhancementScopes.add(scope);

            if (this.circleFrame !== null) {
                return;
            }

            this.circleFrame = window.requestAnimationFrame(() => {
                this.circleFrame = null;
                const scopes = Array.from(this.enhancementScopes);
                this.enhancementScopes.clear();

                scopes.forEach(function (pendingScope) {
                    if (!pendingScope || !pendingScope.isConnected) {
                        return;
                    }
                    if (typeof window.applyCircleType === 'function') {
                        window.applyCircleType($(pendingScope));
                    }
                    if (typeof window.rebindTooltips === 'function') {
                        window.rebindTooltips($(pendingScope));
                    }
                });
            });
        }

        destroyCirclesIn(scope) {
            if (scope && typeof window.destroyCircleTypesIn === 'function') {
                window.destroyCircleTypesIn($(scope));
            }
        }

        toggleActiveCard(cardElement) {
            if (!cardElement) {
                return;
            }
            const wasActive = cardElement.classList.contains('active');
            document.querySelectorAll('.card.active').forEach(function (element) {
                if (element !== cardElement) {
                    element.classList.remove('active');
                }
            });
            cardElement.classList.toggle('active', !wasActive);
        }

        handleCollectionCardKeydown(event) {
            const currentIndex = parseInteger(event.currentTarget.dataset.collectionIndex, -1);
            if (currentIndex < 0) {
                return;
            }

            let nextIndex = currentIndex;
            if (event.key === 'ArrowRight') {
                nextIndex += 1;
            } else if (event.key === 'ArrowLeft') {
                nextIndex -= 1;
            } else if (event.key === 'ArrowDown') {
                nextIndex += this.columns;
            } else if (event.key === 'ArrowUp') {
                nextIndex -= this.columns;
            } else {
                return;
            }

            nextIndex = Math.max(0, Math.min(this.state.filteredCards.length - 1, nextIndex));
            if (nextIndex === currentIndex) {
                return;
            }
            event.preventDefault();
            const nextCard = this.state.filteredCards[nextIndex];
            if (!nextCard || !this.scrollToCard(nextCard.id)) {
                return;
            }
            window.requestAnimationFrame(() => {
                const cardElement = this.visibleCards.querySelector(
                    '.card[data-id="' + nextCard.id + '"]'
                );
                if (cardElement) {
                    cardElement.focus({ preventScroll: true });
                }
            });
        }

        toggleDeckCard(cardId) {
            if (this.state.deckCardIds.has(cardId)) {
                this.removeCardFromDeck(cardId);
            } else {
                this.addCardToDeck(cardId);
            }
        }

        addCardToDeck(cardId) {
            const deck = this.state.decksById.get(this.state.currentDeckId);
            if (
                !deck
                || !this.state.cardsById.has(cardId)
                || this.state.deckCardIds.has(cardId)
                || deck.cards.length >= MAX_DECK_SIZE
            ) {
                return;
            }

            deck.cards.push({ id: cardId, position: deck.cards.length });
            this.afterDeckMutation(deck);
        }

        removeCardFromDeck(cardId) {
            const deck = this.state.decksById.get(this.state.currentDeckId);
            if (!deck || !this.state.deckCardIds.has(cardId)) {
                return;
            }

            deck.cards = deck.cards
                .filter(function (entry) {
                    return entry.id !== cardId;
                })
                .map(function (entry, index) {
                    return { id: entry.id, position: index };
                });
            this.afterDeckMutation(deck);
        }

        afterDeckMutation(deck) {
            this.state.deckCardIds = new Set(deck.cards.map(function (entry) {
                return entry.id;
            }));
            this.dataVersion += 1;
            this.lastRenderKey = '';
            this.renderDeck();
            this.renderVisibleCards(true);
            this.updateDeckUi();
            this.resetDeckQuickFilters();
            this.updateDeckQuickFilters();
            this.stageCurrentDeckSave().catch((error) => {
                this.showDeckStatus(error && error.message ? error.message : this.labels.saveError);
            });
            $(document).trigger('collection:deck-changed', [deck.id, deck.cards.length]);
        }

        syncCurrentDeckFromDom() {
            const deck = this.state.decksById.get(this.state.currentDeckId);
            if (!deck || !this.deckContainer) {
                return;
            }

            const seenIds = new Set();
            const cards = Array.from(this.deckContainer.querySelectorAll(':scope > .card'))
                .map(function (cardElement, index) {
                    return {
                        id: parseInteger(cardElement.dataset.id, 0),
                        position: index
                    };
                })
                .filter((entry) => {
                    if (
                        entry.id <= 0
                        || !this.state.cardsById.has(entry.id)
                        || seenIds.has(entry.id)
                    ) {
                        return false;
                    }
                    seenIds.add(entry.id);
                    return true;
                })
                .slice(0, MAX_DECK_SIZE)
                .map(function (entry, index) {
                    return { id: entry.id, position: index };
                });

            deck.cards = cards;
            this.state.deckCardIds = new Set(cards.map(function (entry) {
                return entry.id;
            }));
        }

        updateDeckUi() {
            const count = this.state.deckCardIds.size;
            const progress = document.getElementById('deck-progress');
            const quickBattle = document.getElementById('collection-quick-battle');

            if (progress) {
                const max = parseInteger(progress.dataset.max, MAX_DECK_SIZE);
                const isReady = count >= max;
                const labelTemplate = progress.dataset.labelTemplate
                    || translate('js.deck.count', 'Deck {count}/{max}', {
                        count: '__COUNT__',
                        max: '__MAX__'
                    });
                const readyLabel = progress.dataset.readyLabel
                    || translate('js.deck.ready', 'Deck ready');

                progress.dataset.count = String(count);
                progress.classList.toggle('is-ready', isReady);
                progress.textContent = isReady
                    ? readyLabel
                    : labelTemplate
                        .replace('__COUNT__', String(count))
                        .replace('__MAX__', String(max));
            }

            if (quickBattle) {
                const isReady = count >= MAX_DECK_SIZE;
                quickBattle.classList.toggle('is-disabled', !isReady);
                quickBattle.setAttribute('aria-disabled', isReady ? 'false' : 'true');
                quickBattle.title = isReady
                    ? ''
                    : translate(
                        'js.deck.complete_first',
                        'Add 6 cards to your deck before starting a battle.'
                    );
            }

            if (typeof window.updateDeckProgress === 'function') {
                window.updateDeckProgress();
            }
        }

        openCardZoom(cardId) {
            const card = this.state.cardsById.get(cardId);
            if (!card || typeof window.zoomCard !== 'function' || typeof window.handleLightbox !== 'function') {
                return;
            }

            const sourceCard = this.createCardElement(card, this.state.deckCardIds.has(card.id));
            const zoomedCard = window.zoomCard($(sourceCard));
            window.handleLightbox(zoomedCard);
            zoomedCard.on('click', function (event) {
                event.stopPropagation();
            });
        }

        deckSignature(cards) {
            return JSON.stringify((cards || []).map(function (entry, index) {
                return {
                    id: parseInteger(entry.id, 0),
                    position: index
                };
            }));
        }

        snapshotCurrentDeck() {
            this.syncCurrentDeckFromDom();
            const deck = this.state.decksById.get(this.state.currentDeckId);
            if (!deck) {
                return null;
            }

            return {
                deckId: deck.id,
                cards: deck.cards.map(function (entry, index) {
                    return { id: entry.id, position: index };
                }),
                signature: this.deckSignature(deck.cards)
            };
        }

        stageCurrentDeckSave() {
            const snapshot = this.snapshotCurrentDeck();
            if (!snapshot) {
                return Promise.resolve({ success: true, unchanged: true });
            }

            if (
                snapshot.signature === this.persistedDeckSignatures.get(snapshot.deckId)
                && !this.pendingSave
            ) {
                return Promise.resolve({
                    success: true,
                    unchanged: true,
                    deck_id: snapshot.deckId
                });
            }

            this.pendingSave = snapshot;
            if (!this.pendingSavePromise) {
                this.pendingSavePromise = new Promise((resolve, reject) => {
                    this.pendingSaveResolve = resolve;
                    this.pendingSaveReject = reject;
                });
            }

            window.clearTimeout(this.saveTimer);
            this.saveTimer = window.setTimeout(() => {
                this.flushPendingDeckSave().catch(() => {
                    // The staged promise reports the error to the interaction
                    // that produced the snapshot. Keep the persisted signature
                    // unchanged so the next save attempt retries it.
                });
            }, SAVE_DEBOUNCE_MS);
            return this.pendingSavePromise;
        }

        saveCurrentDeck(options) {
            const settings = options || {};
            const promise = this.stageCurrentDeckSave();
            if (settings.immediate !== false) {
                const flushPromise = this.flushPendingDeckSave();
                return Promise.all([promise, flushPromise]).then(() => this.operationChain);
            }
            return promise;
        }

        flushPendingDeckSave() {
            window.clearTimeout(this.saveTimer);
            this.saveTimer = null;

            if (!this.pendingSave) {
                return Promise.resolve({ success: true, unchanged: true });
            }

            const snapshot = this.pendingSave;
            const resolvePending = this.pendingSaveResolve;
            const rejectPending = this.pendingSaveReject;
            this.pendingSave = null;
            this.pendingSavePromise = null;
            this.pendingSaveResolve = null;
            this.pendingSaveReject = null;

            const request = this.enqueueOperation(() => this.persistDeckSnapshot(snapshot));
            request.then(resolvePending, rejectPending);
            return request;
        }

        enqueueOperation(operation) {
            const request = this.operationChain
                .catch(function () {
                    return undefined;
                })
                .then(operation);
            this.operationChain = request.catch(function () {
                return undefined;
            });
            return request;
        }

        async persistDeckSnapshot(snapshot) {
            if (snapshot.signature === this.persistedDeckSignatures.get(snapshot.deckId)) {
                return {
                    success: true,
                    unchanged: true,
                    deck_id: snapshot.deckId
                };
            }

            const body = new URLSearchParams();
            body.set('deck_id', String(snapshot.deckId));
            body.set('deck', JSON.stringify(snapshot.cards));
            const response = await window.fetch(this.endpoints.updateDeck, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: body.toString()
            });
            const payload = await this.readJsonResponse(response);

            if (!response.ok || !payload || payload.success !== true) {
                throw new Error((payload && payload.error) || this.labels.saveError);
            }

            this.persistedDeckSignatures.set(snapshot.deckId, snapshot.signature);
            this.clearDeckStatus();
            return payload;
        }

        async switchDeck(nextDeckId) {
            if (
                nextDeckId <= 0
                || !this.state.decksById.has(nextDeckId)
            ) {
                if (this.deckSelect && this.state.currentDeckId > 0) {
                    this.deckSelect.value = String(this.state.currentDeckId);
                }
                return { success: true, unchanged: true };
            }
            if (nextDeckId === this.state.currentDeckId && !this.deckSwitchInProgress) {
                return { success: true, unchanged: true };
            }

            const sequence = ++this.deckSwitchSequence;
            this.deckSwitchInProgress = true;
            this.setDeckLoading(true);

            try {
                await this.saveCurrentDeck({ immediate: true });

                const responsePayload = await this.enqueueOperation(async () => {
                    if (sequence !== this.deckSwitchSequence) {
                        return { stale: true };
                    }

                    const body = new URLSearchParams();
                    body.set('deck_id', String(nextDeckId));
                    const response = await window.fetch(this.endpoints.activateDeck, {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: body.toString()
                    });
                    const data = await this.readJsonResponse(response);
                    if (!response.ok || !data || data.success !== true) {
                        if (data && data.error) {
                            console.warn('[Collection] Deck activation rejected:', data.error);
                        }
                        throw new Error(this.labels.loadError);
                    }
                    return data;
                });

                if (
                    sequence !== this.deckSwitchSequence
                    || !responsePayload
                    || responsePayload.stale
                ) {
                    return { success: true, stale: true };
                }

                this.applyActivatedDeck(nextDeckId, responsePayload.deck);
                this.clearDeckStatus();
                return responsePayload;
            } catch (error) {
                if (sequence === this.deckSwitchSequence) {
                    if (this.deckSelect) {
                        this.deckSelect.value = String(this.state.currentDeckId);
                    }
                    this.showDeckStatus(error && error.message ? error.message : this.labels.loadError);
                }
                throw error;
            } finally {
                if (sequence === this.deckSwitchSequence) {
                    this.deckSwitchInProgress = false;
                    this.setDeckLoading(false);
                }
            }
        }

        applyActivatedDeck(deckId, serverDeck) {
            const deck = this.state.decksById.get(deckId);
            if (!deck) {
                return;
            }

            if (serverDeck && Array.isArray(serverDeck.cards)) {
                const normalized = this.normalizeDecks([serverDeck], this.state.cardsById)[0];
                if (normalized) {
                    deck.name = normalized.name || deck.name;
                    deck.eloRating = normalized.eloRating;
                    deck.cards = normalized.cards;
                    this.persistedDeckSignatures.set(deck.id, this.deckSignature(deck.cards));
                }
            }

            this.state.decks.forEach(function (candidate) {
                candidate.active = candidate.id === deckId;
            });
            this.state.currentDeckId = deckId;
            this.state.deckCardIds = new Set(deck.cards.map(function (entry) {
                return entry.id;
            }));
            this.dataVersion += 1;
            this.lastRenderKey = '';

            if (this.deckSelect) {
                this.deckSelect.value = String(deckId);
            }
            this.resetDeckQuickFilters();
            this.renderDeck();
            this.renderVisibleCards(true);
            this.updateDeckUi();
            this.updateDeckQuickFilters();
            this.replaceDeckUrl(deckId);
            $(document).trigger('collection:deck-switched', [deckId]);
        }

        resetDeckQuickFilters() {
            const $toggles = $('#deck-race-filter, #deck-level-filter');
            $toggles.each(function () {
                const $toggle = $(this);
                if ($toggle.is(':checked')) {
                    $toggle.prop('checked', false).trigger('change');
                }
                $toggle.closest('.deck-quick-checkbox').prop('hidden', true);
            });
            $toggles.closest('.deck-quick-filters').prop('hidden', true);
        }

        updateDeckQuickFilters() {
            const $container = $('.deck-quick-filters').first();
            const $raceToggle = $('#deck-race-filter');
            const $levelToggle = $('#deck-level-filter');
            const deck = this.state.decksById.get(this.state.currentDeckId);

            if (!$container.length || !deck || deck.cards.length !== MAX_DECK_SIZE) {
                $container.prop('hidden', true);
                return;
            }

            const deckCards = deck.cards
                .map((entry) => this.state.cardsById.get(entry.id))
                .filter(Boolean);
            const totalCards = this.state.allCards.length;
            let visibleFilters = 0;

            const deckRaces = new Set(deckCards.map((card) => card.race));
            const deckRace = deckRaces.size === 1 ? Array.from(deckRaces)[0] : '';
            if ($raceToggle.length) {
                const raceMatches = deckRace
                    ? this.state.allCards.filter((card) => card.race === deckRace).length
                    : 0;
                const showRace = Boolean(deckRace) && raceMatches < totalCards;
                const $raceItem = $raceToggle.closest('.deck-quick-checkbox');
                $raceToggle.attr('data-race', deckRace);
                $raceItem.prop('hidden', !showRace);
                if (showRace) {
                    visibleFilters += 1;
                    const raceLabel = this.raceLabels[deckRace]
                        || translate('races.' + deckRace, deckRace);
                    const raceTemplate = String($raceItem.attr('data-label-template') || '__RACE__');
                    $raceItem.find('.deck-quick-filter__label, > span:not(.deck-quick-filter__switch)').first()
                        .text(raceTemplate.replace(/__RACE__/g, raceLabel));
                }
            }

            let levelFilterMax = null;
            const maxLevel = deckCards.reduce((max, card) => Math.max(max, card.level), -1);
            if (maxLevel >= 0 && maxLevel <= 4) {
                levelFilterMax = maxLevel <= 2 ? 2 : (maxLevel <= 3 ? 3 : 4);
            }
            if ($levelToggle.length) {
                const levelMatches = levelFilterMax === null
                    ? 0
                    : this.state.allCards.filter((card) => card.level <= levelFilterMax).length;
                const showLevel = levelFilterMax !== null && levelMatches < totalCards;
                const $levelItem = $levelToggle.closest('.deck-quick-checkbox');
                $levelToggle.attr('data-level-max', levelFilterMax === null ? '' : String(levelFilterMax));
                $levelItem.prop('hidden', !showLevel);
                if (showLevel) {
                    visibleFilters += 1;
                    const levelLabel = String(
                        $levelItem.attr('data-level-label-' + levelFilterMax) || levelFilterMax
                    );
                    const levelTemplate = String($levelItem.attr('data-label-template') || '__LEVEL__');
                    $levelItem.find('.deck-quick-filter__label, > span:not(.deck-quick-filter__switch)').first()
                        .text(levelTemplate.replace(/__LEVEL__/g, levelLabel));
                }
            }

            $container.toggleClass('deck-quick-filters--multiple', visibleFilters > 1);
            $container.prop('hidden', visibleFilters === 0);
            $(document).trigger('collection:quick-filters-updated');
        }

        replaceDeckUrl(deckId) {
            if (!window.history || typeof window.history.replaceState !== 'function') {
                return;
            }

            const url = new URL(window.location.href);
            url.searchParams.set('id_deck', String(deckId));
            url.searchParams.delete('id_deck_delete');
            url.searchParams.delete('id_deck_rename');
            url.searchParams.delete('new_name');
            window.history.replaceState({ deckId: deckId }, '', url.pathname + url.search + url.hash);
        }

        setDeckLoading(loading) {
            const quickBattle = document.getElementById('collection-quick-battle');
            if (this.deckSelect) {
                this.deckSelect.disabled = loading;
                this.deckSelect.setAttribute('aria-busy', loading ? 'true' : 'false');
            }
            if (this.deckBuilding) {
                this.deckBuilding.setAttribute('aria-busy', loading ? 'true' : 'false');
            }
            if (this.deckLoadingIndicator) {
                this.deckLoadingIndicator.hidden = !loading;
                this.deckLoadingIndicator.textContent = this.labels.loading;
            }
            if (quickBattle) {
                quickBattle.classList.toggle('is-deck-switching', loading);
                if (loading) {
                    quickBattle.setAttribute('aria-disabled', 'true');
                } else {
                    this.updateDeckUi();
                }
            }
        }

        showDeckStatus(message) {
            if (!this.deckStatus) {
                console.error('[Collection] ' + message);
                return;
            }
            this.deckStatus.hidden = false;
            this.deckStatus.textContent = message;
        }

        clearDeckStatus() {
            if (!this.deckStatus) {
                return;
            }
            this.deckStatus.hidden = true;
            this.deckStatus.textContent = '';
        }

        async readJsonResponse(response) {
            try {
                return await response.json();
            } catch (error) {
                return null;
            }
        }

        getRenderedCardCount() {
            return this.visibleCards
                ? this.visibleCards.querySelectorAll(':scope > .card').length
                : 0;
        }

        destroy() {
            this.destroyed = true;
            window.clearTimeout(this.saveTimer);
            if (this.renderFrame !== null) {
                window.cancelAnimationFrame(this.renderFrame);
            }
            if (this.circleFrame !== null) {
                window.cancelAnimationFrame(this.circleFrame);
            }
            window.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onResize);
            window.removeEventListener('orientationchange', this.onResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this.onResize);
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            $(this.visibleCards).off('.virtualCollection');
            $(this.deckContainer).off('.virtualCollection');
            if (this.deckSelect) {
                $(this.deckSelect).off('.virtualCollection');
            }
            const sortSelect = document.getElementById('sort-by-select');
            if (sortSelect) {
                $(sortSelect).off('.virtualCollection');
            }
            $(document).off('.virtualCollection');
            this.destroyCirclesIn(this.visibleCards);
            this.destroyCirclesIn(this.deckContainer);
        }
    }

    $(function () {
        const payload = readPayload();
        if (!payload || !document.getElementById('collection-viewport')) {
            return;
        }

        const app = new CollectionApp(payload);
        if (!app.init()) {
            console.error('[Collection] Required virtual collection elements are missing.');
        }
    });

    window.CollectionApp = CollectionApp;
})(window, document, window.jQuery);
